import { useEffect, useState } from 'react'
import {
  getNotificationSettings,
  addSubmissionRecipient,
  removeSubmissionRecipient,
  addFailureRecipient,
  removeFailureRecipient,
  getAdminUsers,
  addAdminUser,
  removeAdminUser,
} from '../api/admin'
import { ApiError } from '../api/client'
import type { AdminNotificationSettings, AdminUserRow } from '../api/adminTypes'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { inputClassName } from '../components/fields/fieldStyles'
import { useAdminSession } from './session'
import { LoadingNote, ErrorNote } from './Notes'

// Settings (issue #78): the two admin-editable notification recipient lists
// that replaced the old deploy-time SUBMISSION_NOTIFY_EMAILS env var —
// "every submission" and "Salesforce push failed/partial" now have
// independent audiences, editable here without a redeploy.
export function SettingsPage() {
  const { user } = useAdminSession()
  const [settings, setSettings] = useState<AdminNotificationSettings | null>(null)
  const [error, setError] = useState(false)
  const canEdit = user?.role === 'admin'

  useEffect(() => {
    setError(false)
    getNotificationSettings()
      .then(setSettings)
      .catch(() => setError(true))
  }, [])

  return (
    <div>
      <h1
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title-lg)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-6) 0',
        }}
      >
        Settings
      </h1>

      {error && <ErrorNote>Something went wrong loading notification settings.</ErrorNote>}
      {settings === null && !error && <LoadingNote>Loading settings…</LoadingNote>}

      {settings !== null && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-6)' }}>
          <RecipientList
            title="Submission notifications"
            description="Every one of these addresses gets a full copy of the application whenever an applicant submits. An empty list simply means these emails are off."
            emails={settings.submission_recipients}
            canEdit={canEdit}
            allowEmpty
            onAdd={(email) => addSubmissionRecipient(email).then(setSettings)}
            onRemove={(email) => removeSubmissionRecipient(email).then(setSettings)}
          />
          <RecipientList
            title="Failure alerts"
            description="Sent only when a submission's Salesforce push fails or partially fails — the only proactive signal that a record didn't land. At least one recipient is required."
            emails={settings.failure_recipients}
            canEdit={canEdit}
            allowEmpty={false}
            onAdd={(email) => addFailureRecipient(email).then(setSettings)}
            onRemove={(email) => removeFailureRecipient(email).then(setSettings)}
          />
          <AdminUsersCard canEdit={canEdit} currentEmail={user?.email ?? ''} />
        </div>
      )}
    </div>
  )
}

// Who may sign in to this console, and as what. Replaces adding rows to the
// admin_users table by hand: admins manage the list here, viewers see it.
function AdminUsersCard({ canEdit, currentEmail }: { canEdit: boolean; currentEmail: string }) {
  const [admins, setAdmins] = useState<AdminUserRow[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [draft, setDraft] = useState('')
  const [role, setRole] = useState<'admin' | 'viewer'>('viewer')
  const [adding, setAdding] = useState(false)
  const [removingEmail, setRemovingEmail] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    getAdminUsers()
      .then((res) => setAdmins(res.admins))
      .catch(() => setLoadError(true))
  }, [])

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    const email = draft.trim()
    if (!email) return
    setAdding(true)
    setFormError(null)
    try {
      const res = await addAdminUser(email, role)
      setAdmins(res.admins)
      setDraft('')
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not add that user. Please try again.')
    } finally {
      setAdding(false)
    }
  }

  async function handleRemove(email: string) {
    setRemovingEmail(email)
    setFormError(null)
    try {
      const res = await removeAdminUser(email)
      setAdmins(res.admins)
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not remove that user. Please try again.')
    } finally {
      setRemovingEmail(null)
    }
  }

  const textStyle = {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-body-sm)',
    color: 'var(--color-text-secondary)',
  } as const

  return (
    <Card padding="md">
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-2) 0',
        }}
      >
        Admin users
      </h2>
      <p style={{ ...textStyle, margin: '0 0 var(--spacing-4) 0' }}>
        Who can sign in to this console with Google. Admins can change data and settings; viewers are read-only.
        Only company Google accounts (the allowed sign-in domains) can be added.
      </p>

      {loadError && <ErrorNote>Something went wrong loading admin users.</ErrorNote>}
      {admins === null && !loadError && <LoadingNote>Loading admin users…</LoadingNote>}

      {admins !== null && (
        <ul style={{ listStyle: 'none', margin: '0 0 var(--spacing-4) 0', padding: 0 }}>
          {admins.map((a) => (
            <li
              key={a.email}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--spacing-3)',
                padding: '10px 0',
                borderBottom: '1px solid var(--color-outline-subtle)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-sm)',
                color: 'var(--color-text-primary)',
              }}
            >
              <span>
                {a.email}
                {a.email === currentEmail && <span style={textStyle}> (you)</span>}
                <span style={textStyle}>
                  {' · '}
                  {a.role}
                  {' · '}
                  {a.last_login_at ? 'last sign-in ' + new Date(a.last_login_at).toLocaleDateString() : 'never signed in'}
                </span>
              </span>
              {canEdit && a.email !== currentEmail && (
                <Button
                  variant="danger-text"
                  onClick={() => handleRemove(a.email)}
                  loading={removingEmail === a.email}
                  disabled={removingEmail !== null && removingEmail !== a.email}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {formError && (
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-error-text)',
            margin: '0 0 var(--spacing-3) 0',
          }}
        >
          {formError}
        </p>
      )}

      {canEdit && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
          <input
            type="email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="name@zota.com"
            disabled={adding}
            className={inputClassName}
            style={{ flex: 1, height: '36px' }}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}
            disabled={adding}
            className={inputClassName}
            style={{ height: '36px', width: '110px' }}
            aria-label="Role"
          >
            <option value="viewer">Viewer</option>
            <option value="admin">Admin</option>
          </select>
          <Button type="submit" variant="secondary" size="sm" loading={adding} disabled={!draft.trim()}>
            Add
          </Button>
        </form>
      )}
    </Card>
  )
}

function RecipientList({
  title,
  description,
  emails,
  canEdit,
  allowEmpty,
  onAdd,
  onRemove,
}: {
  title: string
  description: string
  emails: string[]
  canEdit: boolean
  allowEmpty: boolean
  onAdd: (email: string) => Promise<void>
  onRemove: (email: string) => Promise<void>
}) {
  const [draft, setDraft] = useState('')
  const [adding, setAdding] = useState(false)
  const [removingEmail, setRemovingEmail] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    const email = draft.trim()
    if (!email) return
    setAdding(true)
    setFormError(null)
    try {
      await onAdd(email)
      setDraft('')
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not add that address. Please try again.')
    } finally {
      setAdding(false)
    }
  }

  async function handleRemove(email: string) {
    setRemovingEmail(email)
    setFormError(null)
    try {
      await onRemove(email)
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not remove that address. Please try again.')
    } finally {
      setRemovingEmail(null)
    }
  }

  const warnEmpty = !allowEmpty && emails.length === 0

  return (
    <Card padding="md">
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-2) 0',
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-body-sm)',
          color: 'var(--color-text-secondary)',
          margin: '0 0 var(--spacing-4) 0',
        }}
      >
        {description}
      </p>

      {warnEmpty && (
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-warning-text)',
            margin: '0 0 var(--spacing-4) 0',
          }}
        >
          No recipients configured — these alerts are currently silent.
        </p>
      )}

      {emails.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--spacing-4) 0',
          }}
        >
          No recipients yet.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: '0 0 var(--spacing-4) 0', padding: 0 }}>
          {emails.map((email) => (
            <li
              key={email}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 0',
                borderBottom: '1px solid var(--color-outline-subtle)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-sm)',
                color: 'var(--color-text-primary)',
              }}
            >
              {email}
              {canEdit && (
                <Button
                  variant="danger-text"
                  onClick={() => handleRemove(email)}
                  loading={removingEmail === email}
                  disabled={removingEmail !== null && removingEmail !== email}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {formError && (
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-error-text)',
            margin: '0 0 var(--spacing-3) 0',
          }}
        >
          {formError}
        </p>
      )}

      {canEdit && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
          <input
            type="email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="name@example.com"
            disabled={adding}
            className={inputClassName}
            style={{ flex: 1, height: '36px' }}
          />
          <Button type="submit" variant="secondary" size="sm" loading={adding} disabled={!draft.trim()}>
            Add
          </Button>
        </form>
      )}
    </Card>
  )
}
